import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { QuickView } from "./QuickView";
import { useLanguage } from "@/context/LanguageContext";

export const ProductCard = ({ product, index = 0 }) => {
  const { t } = useLanguage();
  const { addItem } = useCart();
  const [quickOpen, setQuickOpen] = useState(false);
  const onSale = !!product.sale_price;
  const soldOut = product.stock <= 0;
  const discount = onSale ? Math.round((1 - product.sale_price / product.price) * 100) : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    if (soldOut) return;
    const needsOptions = (product.sizes?.length > 1) || (product.colors?.length > 1);
    if (needsOptions) {
      setQuickOpen(true);
      return;
    }
    addItem(product, {
      quantity: 1,
      size: product.sizes?.[0] || null,
      color: product.colors?.[0] || null,
    });
    toast.success(`${product.name} ${t("added to bag")}`);
  };

  return (
    <>
      <div
        className="group animate-fade-up"
        style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
        data-testid={`product-card-${product.id}`}
      >
        <Link to={`/product/${product.id}`} className="block">
          <div className="relative overflow-hidden bg-secondary aspect-[4/5]">
            <img
              src={product.images?.[0]}
              alt={product.name}
              loading="lazy"
              className="hover-zoom-img h-full w-full object-cover"
            />

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {onSale && (
                <span className="bg-gold text-gold-foreground text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1" data-testid="sale-badge">
                  −{discount}%
                </span>
              )}
              {product.new_arrival && !onSale && (
                <span className="bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1">
                  {t("New")}
                </span>
              )}
            </div>

            {soldOut && (
              <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                <span className="overline border border-foreground px-4 py-2">{t("Sold Out")}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
              <button
                onClick={(e) => { e.preventDefault(); setQuickOpen(true); }}
                className="h-9 w-9 grid place-items-center bg-background/85 backdrop-blur hover:bg-background transition-colors"
                data-testid={`quickview-btn-${product.id}`}
                aria-label="Quick view"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>

            {/* Add to bag */}
            {!soldOut && (
              <button
                onClick={handleAdd}
                className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] py-3.5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 flex items-center justify-center gap-2"
                data-testid={`add-to-bag-${product.id}`}
              >
                <ShoppingBag className="h-3.5 w-3.5" /> {t("Add to Bag")}
              </button>
            )}
          </div>
        </Link>

        <div className="pt-4">
          <p className="overline text-muted-foreground mb-1.5">{product.category}</p>
          <Link to={`/product/${product.id}`}>
            <h3 className="font-serif text-lg leading-tight hover:text-gold transition-colors" data-testid={`product-name-${product.id}`}>
              {product.name}
            </h3>
          </Link>
          <div className="mt-2 flex items-center gap-3">
            {onSale ? (
              <>
                <span className="text-gold font-medium">{formatPrice(product.sale_price)}</span>
                <span className="text-muted-foreground line-through text-sm">{formatPrice(product.price)}</span>
              </>
            ) : (
              <span className="font-medium">{formatPrice(product.price)}</span>
            )}
          </div>
        </div>
      </div>

      <QuickView product={product} open={quickOpen} onOpenChange={setQuickOpen} />
    </>
  );
};
